import React, { useMemo } from 'react';
import { Drawer, Input, Table, Space, Typography, Button } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type {
  MismatchedEntityType,
  MismatchedLocation,
  MismatchedLocationsResponse,
} from '../../services/api';
import { handleEntityLinkClick } from '../MapComponent/utils';

const { Search } = Input;

interface DiagnosisMismatchDrawerProps {
  open: boolean;
  onClose: () => void;
  entityType: MismatchedEntityType | null;
  data?: MismatchedLocationsResponse;
  loading?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: (value: string) => void;
  pagination: { current: number; pageSize: number };
  onPaginationChange: (page: number, pageSize?: number) => void;
  rows: MismatchedLocation[];
  filteredCount: number;
  onEditLocation: (record: MismatchedLocation) => void;
}

const entityLabels: Record<MismatchedEntityType, string> = {
  clients: 'Clients',
  vendors: 'Vendors',
  prospects: 'Prospects',
};

const DiagnosisMismatchDrawer: React.FC<DiagnosisMismatchDrawerProps> = ({
  open,
  onClose,
  entityType,
  data,
  loading,
  searchValue,
  onSearchChange,
  onSearch,
  pagination,
  onPaginationChange,
  rows,
  filteredCount,
  onEditLocation,
}) => {
  const title = entityType ? `${entityLabels[entityType]} Missing Coordinates` : 'Missing Coordinates';
  const totalCount = data?.total_count ?? filteredCount;

  const handleTableChange = (tablePagination: TablePaginationConfig) => {
    const page = tablePagination.current || pagination.current;
    const size = tablePagination.pageSize || pagination.pageSize;
    onPaginationChange(page, size);
  };

  const columns: ColumnsType<MismatchedLocation> = useMemo(
    () => [
      {
        title: 'Client',
        dataIndex: 'company',
        key: 'company',
        render: (value: string, record) =>
          record.userid ? (
            <Typography.Link onClick={(event) => handleEntityLinkClick(event, String(record.userid))}>
              {value || '--'}
            </Typography.Link>
          ) : (
            value || '--'
          ),
        ellipsis: true,
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        render: (value: string, record) => value || record.website_found_address || '--',
      },
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city',
        render: (value: string, record) => value || record.website_found_city || '--',
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (value: string, record) => value || record.website_found_state || '--',
      },
      {
        title: 'Country',
        dataIndex: ['country', 'short_name'],
        key: 'country',
        render: (_, record) => record.country?.short_name || record.website_found_country || '--',
      },
      {
        title: 'Staff Owner',
        dataIndex: 'staff_name',
        key: 'staff_name',
        render: (value: string) => value || '--',
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, record) => (
          <Button size="small" type="primary" onClick={() => onEditLocation(record)}>
            Edit
          </Button>
        ),
      },
    ],
    [onEditLocation],
  );

  return (
    <Drawer
      title={title}
      placement="right"
      width={1000}
      onClose={onClose}
      open={open}
      destroyOnClose
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            Showing {filteredCount.toLocaleString()} of {totalCount.toLocaleString()} records with missing coordinates.
          </Typography.Text>
          <Search
            placeholder="Search by company, city, state..."
            allowClear
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onSearch={onSearch}
            enterButton
          />
        </Space>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Table
          rowKey={(record) => `${record.id}-${record.userid ?? ''}`}
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ y: 'calc(100vh - 300px)' }}
        />
      </div>
    </Drawer>
  );
};

export default DiagnosisMismatchDrawer;
